import React, { useState, useEffect, useRef } from "react";
import config from "../config";
import "./FunctionSelector.css";
import downCaret from "../public/img/down_caret.svg";

const FunctionSelector = ({ selectedFunctions, onChange }) => {
  const [availableFunctions, setAvailableFunctions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);

  // Fetch available functions when component mounts
  useEffect(() => {
    fetchFunctions();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch available functions from API
  const fetchFunctions = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.chatbot.base}/functions`,
        { credentials: "include" }
      );

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success && Array.isArray(data.functions)) {
        setAvailableFunctions(data.functions);
      } else {
        throw new Error(data.error || "Failed to load functions");
      }
    } catch (err) {
      console.error("Error fetching functions:", err);
      setError("Failed to load available functions. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Toggle function selection
  const toggleFunctionSelection = (func) => {
    const isSelected = selectedFunctions.some((f) => f.name === func.name);

    if (isSelected) {
      onChange(selectedFunctions.filter((f) => f.name !== func.name));
    } else {
      onChange([...selectedFunctions, func]);
    }
  };

  // Filter functions based on search query
  const filteredFunctions = availableFunctions.filter(
    (func) =>
      func.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      func.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="function-selector" ref={dropdownRef}>
      <div className="function-selector-header" onClick={toggleDropdown}>
        <span>{selectedFunctions.length} function(s) selected</span>
        <img
          src={downCaret}
          alt="toggle"
          className={`dropdown-caret ${isOpen ? "open" : ""}`}
        />
      </div>

      {isOpen && (
        <div className="function-dropdown">
          <div className="function-search">
            <input
              type="text"
              placeholder="Search functions..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>

          {/* Selected functions tags inside dropdown */}
          {selectedFunctions.length > 0 && (
            <div className="selected-functions inside-dropdown">
              <div className="selected-functions-label">
                Selected functions:
              </div>
              <div className="selected-functions-list">
                {selectedFunctions.map((func) => (
                  <div key={func.name} className="selected-function-item">
                    <span className="selected-function-name">{func.name}</span>
                    <button
                      type="button"
                      className="remove-function-btn"
                      onClick={() => toggleFunctionSelection(func)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="function-loading">Loading functions...</div>
          ) : error ? (
            <div className="function-error">{error}</div>
          ) : filteredFunctions.length === 0 ? (
            <div className="function-empty">No functions found</div>
          ) : (
            <div className="function-list">
              {filteredFunctions.map((func) => (
                <div
                  key={func.name}
                  className="function-item"
                  onClick={() => toggleFunctionSelection(func)}
                >
                  <div className="function-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedFunctions.some(
                        (f) => f.name === func.name
                      )}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleFunctionSelection(func);
                      }}
                    />
                  </div>
                  <div className="function-info">
                    <div className="function-name">{func.name}</div>
                    <div className="function-description">
                      {func.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FunctionSelector;
